import * as React from 'react';
import Grid from '@mui/material/Grid2';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BaseAccordion from '../../../components/Accordion';
import BaseButton from '../../../components/BaseButton';
import BaseCheckbox from '../../../components/BaseCheckbox';
import BaseInput from '../../../components/BaseInput';
import BaseSelect from '../../../components/BaseSelect';
import DataStore from '../../../store';
import { validateForm } from '../../../helpers/formValidator';
import { notificationStore } from '../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../helpers/constants';
import { getPageConfiguration } from '../../../helpers/groups';
import { observer } from 'mobx-react';

const MAX_SAMPLE_TYPES = 3;

type SampleGroup = {
  numberOfSamples: string;
  sample_type: string;
  sample_origin: string;
  analysis_target: string;
  storage_condition: string;
  reference_database: string;
};

const createEmptySampleGroup = (): SampleGroup => ({
  numberOfSamples: '',
  sample_type: 'Stool',
  sample_origin: 'Human',
  analysis_target: 'Bacteria',
  storage_condition: 'ZYMO DNA shield',
  reference_database: '',
});

const Ordering = observer(() => {
  const [errors, setErrors] = React.useState({});
  const [sampleLabelsPdf, setSampleLabelsPdf] = React.useState<any>(null);
  const [data, setData] = React.useState({
    erp_customer: '',
    naming_series: 'ST-',
    commercial_research: false,
    create_extracted_dna: false,
    create_labelled_dna: false,
    sample_groups: [createEmptySampleGroup()],
  });

  const fieldLabels: { [key: string]: string } = {
    numberOfSamples: 'Number of samples',
    erp_customer: 'Customer',
    sample_type: 'Sample type',
    naming_series: 'Sample Series',
    sample_origin: 'Sample origin',
    analysis_target: 'Analysis Target',
    storage_condition: 'Storage condition',
    reference_database: 'Reference database',
    commercial_research: 'Commercial research',
    create_extracted_dna: 'Create extracted DNA',
    create_labelled_dna: 'Create labelled DNA',
  };

  const sampleTypes = ['Stool', 'DNA', 'Fermentation', 'Mock microbiome'];
  const sampleSeries = ['ST-', 'RD-'];
  const sampleOrigins = [
    'Human',
    'Mouse',
    'Cell culture',
    'Fermentation',
    'Rat',
  ];
  const analysisTargets = ['Bacteria', 'Yeast'];
  const storageConditions = [
    'Other buffer',
    'Norgen Stool Preservative',
    'Canvax',
    'Microbiotix buffer',
    'TE Buffer',
    'Frozen',
    'ZYMO DNA shield',
    'DNA Genotek Omnigene',
  ];
  const getReferenceDatabaseValues = (): string[] => {
    const pageConfiguration: any = getPageConfiguration('ordering');
    const labels: string[] =
      pageConfiguration?.referenceDatabase?.map((item: any) => item.label) ||
      [];
    return ['', ...labels.filter((label) => label !== '')];
  };
  const canAddSampleGroup = data.sample_groups.length < MAX_SAMPLE_TYPES;
  const isDnaCreationAvailable = data.naming_series === 'RD-';
  const isCommercialResearchAvailable = data.naming_series === 'ST-';
  const isAdvancedAvailable =
    isDnaCreationAvailable || isCommercialResearchAvailable;

  const validationSchema = {
    erp_customer: {
      type: 'string',
      required: false,
    },
    naming_series: {
      type: 'string',
      required: true,
    },
    create_extracted_dna: {
      type: 'boolean',
      required: false,
    },
    create_labelled_dna: {
      type: 'boolean',
      required: false,
    },
    commercial_research: {
      type: 'boolean',
      required: false,
    },
  };

  const onChangeData = (event: any) => {
    const { name, value } = event.target;
    setData((prevData) => {
      if (name === 'naming_series') {
        return {
          ...prevData,
          naming_series: value,
          commercial_research:
            value === 'ST-' ? prevData.commercial_research : false,
          create_extracted_dna:
            value === 'RD-' ? prevData.create_extracted_dna : false,
          create_labelled_dna:
            value === 'RD-' ? prevData.create_labelled_dna : false,
        };
      }
      return { ...prevData, [name]: value };
    });
  };

  const onChangeGroupField =
    (index: number, field: keyof SampleGroup) => (event: any) => {
      const { value } = event.target;
      setData((prevData) => {
        const nextGroups = [...prevData.sample_groups];
        nextGroups[index] = { ...nextGroups[index], [field]: value };
        return { ...prevData, sample_groups: nextGroups };
      });
    };

  const addSampleGroup = () => {
    setData((prevData) => {
      if (prevData.sample_groups.length >= MAX_SAMPLE_TYPES) {
        return prevData;
      }
      return {
        ...prevData,
        sample_groups: [...prevData.sample_groups, createEmptySampleGroup()],
      };
    });
  };

  const removeSampleGroup = (index: number) => () => {
    setData((prevData) => ({
      ...prevData,
      sample_groups: prevData.sample_groups.filter((_, i) => i !== index),
    }));
  };

  const downloadSampleLabelsPdf = (pdf: any) => {
    if (!pdf?.contentBase64) {
      return;
    }
    const binary = window.atob(pdf.contentBase64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    const blob = new Blob([bytes], {
      type: pdf.mimeType || 'application/pdf',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = pdf.fileName || 'sample-labels.pdf';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  type SampleGroupsErrorDetails = {
    message: string;
    fieldKeys: string[];
  };

  const getSampleGroupsErrorDetails = (): SampleGroupsErrorDetails | null => {
    if (data.sample_groups.length === 0) {
      return { message: 'Add at least one sample type.', fieldKeys: [] };
    }
    const invalidFieldKeys: string[] = [];
    data.sample_groups.forEach((group, index) => {
      const count = Number(group.numberOfSamples);
      if (
        !group.numberOfSamples ||
        !Number.isInteger(count) ||
        count < 1 ||
        count > 500
      ) {
        invalidFieldKeys.push(`number_of_samples_${index}`);
      }
      if (!group.sample_type) {
        invalidFieldKeys.push(`sample_type_${index}`);
      }
      if (!group.sample_origin) {
        invalidFieldKeys.push(`sample_origin_${index}`);
      }
      if (!group.analysis_target) {
        invalidFieldKeys.push(`analysis_target_${index}`);
      }
      if (!group.storage_condition) {
        invalidFieldKeys.push(`storage_condition_${index}`);
      }
    });
    if (invalidFieldKeys.length > 0) {
      return {
        message:
          'Fill in every required field (with a number of samples between ' +
          '1 and 500) for each sample type.',
        fieldKeys: invalidFieldKeys,
      };
    }
    return null;
  };

  const createOrder = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const validError = validateForm(data, validationSchema);
    const sampleGroupsErrorDetails = getSampleGroupsErrorDetails();
    if (validError || sampleGroupsErrorDetails) {
      const humanizedErrors: { [key: string]: string } = Object.entries(
        validError || {}
      ).reduce((acc: any, [key, message]) => {
        acc[fieldLabels[key] || key] = message;
        return acc;
      }, {});
      if (sampleGroupsErrorDetails) {
        humanizedErrors['Sample types'] = sampleGroupsErrorDetails.message;
      }
      notificationStore.showMessage(
        MISSING_FIELDS_MESSAGE,
        'error',
        humanizedErrors
      );
      const sampleGroupFieldErrors = (
        sampleGroupsErrorDetails?.fieldKeys || []
      ).reduce((acc: { [key: string]: string }, key) => {
        acc[key] = sampleGroupsErrorDetails!.message;
        return acc;
      }, {});
      setErrors({
        ...(validError || {}),
        ...sampleGroupFieldErrors,
      });
      return;
    }

    setErrors({});
    setSampleLabelsPdf(null);
    const sampleTypeEntries = data.sample_groups.map((group) => ({
      number_of_samples: Number(group.numberOfSamples),
      sample_type: group.sample_type,
      sample_origin: group.sample_origin,
      analysis_target: group.analysis_target,
      storage_condition: group.storage_condition,
      reference_database: group.reference_database,
    }));
    const resData = await DataStore.sampleManagerCreateOrderRequest({
      erp_customer: data.erp_customer,
      naming_series: data.naming_series,
      commercial_research:
        isCommercialResearchAvailable && data.commercial_research,
      create_extracted_dna: isDnaCreationAvailable && data.create_extracted_dna,
      create_labelled_dna: isDnaCreationAvailable && data.create_labelled_dna,
      sample_type_entries: sampleTypeEntries,
    });
    if (resData?.response) {
      const { sampleLabelsPdf: labelsPdf, ...orderResponse } = resData.response;
      setSampleLabelsPdf(labelsPdf || null);
      if (labelsPdf) {
        downloadSampleLabelsPdf(labelsPdf);
      }
      DataStore.showOutput('Create sample order', orderResponse);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <BaseInput
            label={fieldLabels.erp_customer}
            value={data.erp_customer}
            name='erp_customer'
            onChange={onChangeData}
            errors={errors}
          />
          <BaseSelect
            name='naming_series'
            label={fieldLabels.naming_series}
            values={sampleSeries}
            value={data.naming_series}
            onChange={onChangeData}
            errors={errors}
            required
          />
        </Box>
      </Grid>
      {isAdvancedAvailable && (
        <Grid size={{ xs: 12 }}>
          <BaseAccordion
            key='sample-manager-advanced'
            title={<Typography variant='subtitle1'>Advanced</Typography>}
            sx={{ boxShadow: 0, border: '1px solid #e0e0e0' }}
          >
            <Grid container spacing={1}>
              {isCommercialResearchAvailable && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <BaseCheckbox
                    name='commercial_research'
                    label={fieldLabels.commercial_research}
                    value={data.commercial_research}
                    onChange={onChangeData}
                  />
                </Grid>
              )}
              {isDnaCreationAvailable && (
                <>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <BaseCheckbox
                      name='create_extracted_dna'
                      label={fieldLabels.create_extracted_dna}
                      value={data.create_extracted_dna}
                      onChange={onChangeData}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <BaseCheckbox
                      name='create_labelled_dna'
                      label={fieldLabels.create_labelled_dna}
                      value={data.create_labelled_dna}
                      onChange={onChangeData}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </BaseAccordion>
        </Grid>
      )}
      <Grid size={{ xs: 12 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.sample_groups.map((group, index) => (
            <Box
              key={`sample-group-${index}`}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  mt: -1,
                  mb: 1,
                }}
              >
                {data.sample_groups.length > 1 && (
                  <IconButton
                    aria-label='Remove sample type'
                    size='small'
                    onClick={removeSampleGroup(index)}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BaseSelect
                    name={`sample_type_${index}`}
                    label={fieldLabels.sample_type}
                    values={sampleTypes}
                    value={group.sample_type}
                    onChange={onChangeGroupField(index, 'sample_type')}
                    errors={errors}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BaseInput
                    label={fieldLabels.numberOfSamples}
                    value={group.numberOfSamples}
                    name={`number_of_samples_${index}`}
                    type='number'
                    onChange={onChangeGroupField(index, 'numberOfSamples')}
                    errors={errors}
                    required
                    inputProps={{ min: 1, max: 500, step: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BaseSelect
                    name={`sample_origin_${index}`}
                    label={fieldLabels.sample_origin}
                    values={sampleOrigins}
                    value={group.sample_origin}
                    onChange={onChangeGroupField(index, 'sample_origin')}
                    errors={errors}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BaseSelect
                    name={`analysis_target_${index}`}
                    label={fieldLabels.analysis_target}
                    values={analysisTargets}
                    value={group.analysis_target}
                    onChange={onChangeGroupField(index, 'analysis_target')}
                    errors={errors}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BaseSelect
                    name={`storage_condition_${index}`}
                    label={fieldLabels.storage_condition}
                    values={storageConditions}
                    value={group.storage_condition}
                    onChange={onChangeGroupField(index, 'storage_condition')}
                    errors={errors}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BaseSelect
                    name={`reference_database_${index}`}
                    label={fieldLabels.reference_database}
                    values={getReferenceDatabaseValues()}
                    value={group.reference_database}
                    onChange={onChangeGroupField(index, 'reference_database')}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {canAddSampleGroup ? (
            <BaseButton
              text='Add sample type'
              variant='outlined'
              color='primary'
              onClick={addSampleGroup}
            />
          ) : (
            <Box />
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <BaseButton
              text='Create Order'
              variant='contained'
              color='primary'
              isDisabled={DataStore.isProcessing}
              spinner={DataStore.isProcessing}
              onClick={createOrder}
            />
            {sampleLabelsPdf ? (
              <BaseButton
                text='Download labels PDF'
                variant='outlined'
                color='primary'
                isDisabled={DataStore.isProcessing}
                onClick={() => downloadSampleLabelsPdf(sampleLabelsPdf)}
              />
            ) : null}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
});

export default Ordering;
